import NiceModal, { useModal } from "@ebay/nice-modal-react"
import { Cookie, Eye, ShieldCheck } from "lucide-react"
import { useCallback, useRef, useState } from "react"
import type { EmbeddedBrowserCookieProfile } from "../../electronAPIs/browser"
import { Modal } from "../ui"

export type BrowserStartPromptResponse = { decision: "deny" } | { decision: "allow"; importProfileId?: string }

interface BrowserStartPromptModalProps {
    reason?: string
    cookieProfiles: EmbeddedBrowserCookieProfile[]
}

export function BrowserStartPromptDialog({
    reason,
    cookieProfiles,
    onDecision,
}: BrowserStartPromptModalProps & { onDecision: (response: BrowserStartPromptResponse) => void }): JSX.Element {
    const [copySessions, setCopySessions] = useState(false)
    const [selectedProfileId, setSelectedProfileId] = useState(cookieProfiles[0]?.id ?? "")
    return (
        <Modal title="Allow agent Browser access?" onClose={() => onDecision({ decision: "deny" })} hideSeparator>
            <div className="flex flex-col gap-4 text-sm text-base-content">
                <div className="flex items-start gap-3 border border-primary/30 bg-primary/5 p-3">
                    <ShieldCheck size={18} className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                        <p className="font-medium">The agent wants to start WildLark Browser</p>
                        <p className="mt-1 text-xs leading-relaxed text-muted">
                            It runs in an isolated Browser profile and starts hidden. A Browser item appears in the task sidebar so you can watch or take over
                            at any time.
                        </p>
                    </div>
                </div>

                {reason?.trim() && (
                    <div className="border border-border bg-base-200/40 p-3">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-muted">Why the agent needs it</p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm">{reason.trim()}</p>
                    </div>
                )}

                <div className="flex items-start gap-3 text-xs leading-relaxed text-muted">
                    <Eye size={16} className="mt-0.5 shrink-0" />
                    <p>
                        The agent can read and interact with pages opened in this Browser session. Password, payment, file, and one-time-code fields remain
                        blocked.
                    </p>
                </div>

                {cookieProfiles.length > 0 && (
                    <label className="flex cursor-pointer items-start gap-3 border border-border bg-base-200/40 p-3">
                        <input type="checkbox" className="mt-0.5" checked={copySessions} onChange={(event) => setCopySessions(event.currentTarget.checked)} />
                        <Cookie size={16} className="mt-0.5 shrink-0 text-muted" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-base-content">Copy current website sessions (optional)</span>
                            <span className="mt-1 block text-xs leading-relaxed text-muted">
                                Replaces the current WildLark Browser profile with a fresh profile containing cookies from Chrome or Edge. Existing WildLark
                                Browser sign-ins and site data are deleted. Passwords, history, bookmarks, and autofill are not copied, and the source browser
                                is unchanged.
                            </span>
                            {copySessions && (
                                <select
                                    className="mt-3 h-9 w-full border border-border bg-base-100 px-2 text-xs text-base-content"
                                    aria-label="Browser profile to copy"
                                    value={selectedProfileId}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => setSelectedProfileId(event.currentTarget.value)}
                                >
                                    {cookieProfiles.map((profile) => (
                                        <option key={profile.id} value={profile.id}>
                                            {profile.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </span>
                    </label>
                )}

                <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                        type="button"
                        autoFocus
                        className="btn min-h-10 flex-1 border border-border bg-base-200 px-4 py-2.5 font-medium text-base-content hover:bg-base-300"
                        onClick={() => onDecision({ decision: "deny" })}
                    >
                        Not now
                    </button>
                    <button
                        type="button"
                        className="btn min-h-10 flex-1 bg-primary px-4 py-2.5 font-medium text-primary-content hover:bg-primary/90"
                        onClick={() =>
                            onDecision({
                                decision: "allow",
                                ...(copySessions && selectedProfileId ? { importProfileId: selectedProfileId } : {}),
                            })
                        }
                    >
                        Start Browser
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export const BrowserStartPromptModal = NiceModal.create(({ reason, cookieProfiles }: BrowserStartPromptModalProps) => {
    const modal = useModal()
    const settled = useRef(false)
    const settle = useCallback(
        (response: BrowserStartPromptResponse) => {
            if (settled.current) return
            settled.current = true
            modal.resolve(response)
            modal.remove()
        },
        [modal]
    )
    return <BrowserStartPromptDialog reason={reason} cookieProfiles={cookieProfiles} onDecision={settle} />
})

let startPromptQueue: Promise<void> = Promise.resolve()

export function showBrowserStartPrompt(props: BrowserStartPromptModalProps, signal: AbortSignal): Promise<BrowserStartPromptResponse> {
    const prompt: Promise<BrowserStartPromptResponse> = startPromptQueue.then(async (): Promise<BrowserStartPromptResponse> => {
        if (signal.aborted) throw new DOMException("Browser start was cancelled", "AbortError")
        const response = await new Promise<unknown>((resolve, reject) => {
            const abort = () => {
                void NiceModal.remove(BrowserStartPromptModal)
                reject(new DOMException("Browser start was cancelled", "AbortError"))
            }
            signal.addEventListener("abort", abort, { once: true })
            void NiceModal.show(BrowserStartPromptModal, props)
                .then(resolve, reject)
                .finally(() => signal.removeEventListener("abort", abort))
        })
        if (typeof response !== "object" || response === null || !("decision" in response)) return { decision: "deny" }
        if (response.decision !== "allow") return { decision: "deny" }
        const importProfileId = "importProfileId" in response && typeof response.importProfileId === "string" ? response.importProfileId : undefined
        return { decision: "allow", ...(importProfileId ? { importProfileId } : {}) }
    })
    startPromptQueue = prompt.then(
        () => undefined,
        () => undefined
    )
    return prompt
}
